import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit, AfterViewInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { Chart, ChartConfiguration, ChartType, registerables } from 'chart.js';
import { BoxPlotController, BoxAndWiskers } from '@sgratzl/chartjs-chart-boxplot';
import 'chartjs-adapter-date-fns'; // Import date adapter for time scale support
import { DataService } from '../../services/data.service';
import { ChartDataResponse, DataFilter } from '../../models/chart';

// Register Chart.js components and boxplot
Chart.register(...registerables, BoxPlotController, BoxAndWiskers);

@Component({
  selector: 'app-interactive-chart',
  standalone: true,
  imports: [CommonModule, MatTableModule, MatProgressSpinnerModule, MatIconModule],
  templateUrl: './interactive-chart.component.html',
  styleUrls: ['./interactive-chart.component.scss']
})
export class InteractiveChartComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('chartCanvas', { static: false }) chartCanvas!: ElementRef<HTMLCanvasElement>;
  
  @Input() chartData!: ChartDataResponse;
  @Input() chartType: 'bar' | 'scatter' | 'histogram' | 'kde' | 'line' | 'area' | string = 'bar';
  @Input() dataSourceId?: string;
  @Input() uploadedData?: any[]; // For uploaded file data
  @Input() columnName?: string;
  @Input() secondColumnName?: string;
  @Input() showDataTable: boolean = true;
  @Input() anomalyIndices?: number[]; // Indices of anomalous data points for highlighting
  
  @Output() selectionChange = new EventEmitter<any[]>();
  @Output() chartError = new EventEmitter<string>();
  
  chart?: Chart;
  selectedData: any[] = [];
  isLoadingData: boolean = false;
  errorMessage: string = '';
  displayedColumns: string[] = [];
  
  // Rectangle selection for scatter plots
  private isSelecting: boolean = false;
  private selectionStart: { x: number; y: number } | null = null;
  private selectionEnd: { x: number; y: number } | null = null;
  private selectionDataBounds: { x1: number; x2: number; y1: number; y2: number } | null = null;
  private selectionCanvas: HTMLCanvasElement | null = null;
  private selectionContext: CanvasRenderingContext2D | null = null;
  private resizeObserver: ResizeObserver | null = null;

  constructor(private dataService: DataService) {}

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    if (this.chartData) {
      this.renderChart();
    }
    
    // Set up selection overlay for scatter plots
    if (this.chartType === 'scatter') {
      this.setupSelectionOverlay();
      this.setupResizeObserver();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chartData'] && !changes['chartData'].firstChange) {
      this.renderChart();
    }
    
    // Handle chart type changes
    if (changes['chartType'] && !changes['chartType'].firstChange) {
      // Remove old overlay if exists
      this.removeSelectionOverlay();
      
      // Render chart with new type
      this.renderChart();
      
      // Set up overlay if new type is scatter
      if (this.chartType === 'scatter') {
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          this.setupSelectionOverlay();
          this.setupResizeObserver();
        }, 0);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyChart();
    this.removeSelectionOverlay();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  private renderChart(): void {
    if (!this.chartCanvas || !this.chartData?.data) {
      console.warn('renderChart: missing canvas or chart data', { hasCanvas: !!this.chartCanvas, hasData: !!this.chartData?.data });
      return;
    }

    this.destroyChart();

    const config = this.createChartConfig();
    if (config) {
      this.chart = new Chart(this.chartCanvas.nativeElement, config);
      
      // Set up interaction for scatter charts
      if (this.chartType === 'scatter') {
        this.chartCanvas.nativeElement.classList.add('scatter-chart');
        this.chartCanvas.nativeElement.style.cursor = 'crosshair';
        
        // Set up selection overlay if not already present
        if (!this.selectionCanvas) {
          setTimeout(() => {
            this.setupSelectionOverlay();
            this.setupResizeObserver();
          }, 0);
        }
      } else {
        // Remove scatter-specific features if chart type is not scatter
        this.chartCanvas.nativeElement.classList.remove('scatter-chart');
        this.chartCanvas.nativeElement.style.cursor = '';
      }
    } else {
      console.error('Failed to create chart configuration');
    }
  }

  private createChartConfig(): ChartConfiguration | null {
    if (!this.chartData?.data) return null;

    const data = this.chartData.data;

    // Handle different chart types
    switch (this.chartType) {
      case 'bar':
      case 'histogram':
      case 'hist': // Alias for histogram
        return this.createBarChartConfig(data);
      case 'scatter':
        return this.createScatterChartConfig(data);
      case 'line':
      case 'kde':
        return this.createLineChartConfig(data);
      case 'area':
        return this.createAreaChartConfig(data);
      case 'iqr':
        return this.createBoxPlotConfig(data);
      default:
        console.warn(`Unknown chart type: ${this.chartType}, defaulting to bar chart`);
        return this.createBarChartConfig(data);
    }
  }

  private createBarChartConfig(data: any): ChartConfiguration {
    const labels = data.labels || data.bins?.map((b: any) => b.label) || [];
    const values = data.values || data.bins?.map((b: any) => b.value) || [];

    return {
      type: 'bar' as ChartType,
      data: {
        labels,
        datasets: [{
          label: this.columnName || 'Value',
          data: values,
          backgroundColor: 'rgba(0, 128, 255, 0.6)',
          borderColor: 'rgba(0, 128, 255, 1)',
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        onClick: (event, elements) => this.handleBarClick(event, elements, data),
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                return `Count: ${context.parsed.y}`;
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Frequency'
            }
          },
          x: {
            title: {
              display: true,
              text: this.columnName || 'Value'
            }
          }
        }
      }
    };
  }

  private createScatterChartConfig(data: any): ChartConfiguration {
    const points = data.points || [];
    const xIsDate = data.xIsDate || false;
    const yIsDate = data.yIsDate || false;
    
    // Separate points into normal and anomalous based on anomalyIndices
    const anomalySet = new Set(this.anomalyIndices || []);
    const normalPoints: any[] = [];
    const anomalousPoints: any[] = [];
    
    points.forEach((p: any) => {
      const pointData = { x: p.x, y: p.y, index: p.index };
      if (anomalySet.has(p.index)) {
        anomalousPoints.push(pointData);
      } else {
        normalPoints.push(pointData);
      }
    });
    
    // Create datasets array - only include datasets that have data
    const datasets: any[] = [];
    
    if (normalPoints.length > 0) {
      datasets.push({
        label: `${this.columnName} vs ${this.secondColumnName}`,
        data: normalPoints,
        backgroundColor: 'rgba(0, 128, 255, 0.6)',
        borderColor: 'rgba(0, 128, 255, 1)',
        pointRadius: 4,
        pointHoverRadius: 6
      });
    }
    
    if (anomalousPoints.length > 0) {
      datasets.push({
        label: 'Anomalies',
        data: anomalousPoints,
        backgroundColor: 'rgba(255, 100, 46, 0.7)', // Hot orange from palette (--hot-orange: #FF642E)
        borderColor: 'rgba(255, 100, 46, 1)',
        pointRadius: 5,
        pointHoverRadius: 7
      });
    }
    
    return {
      type: 'scatter' as ChartType,
      data: {
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: anomalousPoints.length > 0 // Only show legend if we have anomalies
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const point = context.raw as { x: number; y: number; index: number };
                const xLabel = xIsDate ? new Date(point.x).toLocaleString() : point.x;
                const yLabel = yIsDate ? new Date(point.y).toLocaleString() : point.y;
                const labels = [
                  `${this.columnName}: ${xLabel}`,
                  `${this.secondColumnName}: ${yLabel}`
                ];
                // Add index information
                if (point.index !== undefined) {
                  labels.push(`Index: ${point.index}`);
                }
                return labels;
              }
            }
          }
        },
        scales: {
          x: {
            type: xIsDate ? 'time' : 'linear',
            title: {
              display: true,
              text: this.columnName || 'X'
            },
            ...(xIsDate && {
              time: {
                displayFormats: {
                  millisecond: 'HH:mm:ss.SSS',
                  second: 'HH:mm:ss',
                  minute: 'HH:mm',
                  hour: 'MMM d, HH:mm',
                  day: 'MMM d',
                  week: 'MMM d',
                  month: 'MMM yyyy',
                  quarter: 'MMM yyyy',
                  year: 'yyyy'
                }
              }
            })
          },
          y: {
            type: yIsDate ? 'time' : 'linear',
            title: {
              display: true,
              text: this.secondColumnName || 'Y'
            },
            ...(yIsDate && {
              time: {
                displayFormats: {
                  millisecond: 'HH:mm:ss.SSS',
                  second: 'HH:mm:ss',
                  minute: 'HH:mm',
                  hour: 'MMM d, HH:mm',
                  day: 'MMM d',
                  week: 'MMM d',
                  month: 'MMM yyyy',
                  quarter: 'MMM yyyy',
                  year: 'yyyy'
                }
              }
            })
          }
        }
      }
    };
  }

  private createLineChartConfig(data: any): ChartConfiguration {
    const xData = data.x || [];
    const yData = data.y || [];
    const xIsDate = data.xIsDate || false;

    return {
      type: 'line' as ChartType,
      data: {
        labels: xData.map((x: any) => x.toString()),
        datasets: [{
          label: this.columnName || 'Density',
          data: yData,
          borderColor: 'rgba(0, 128, 255, 1)',
          backgroundColor: 'rgba(0, 128, 255, 0.2)',
          borderWidth: 2,
          fill: false,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: this.columnName || 'Value'
            },
            ticks: xIsDate ? {
              callback: (value, index) => {
                const timestamp = xData[index];
                return new Date(timestamp).toLocaleDateString();
              }
            } : undefined
          },
          y: {
            title: {
              display: true,
              text: 'Density'
            }
          }
        }
      }
    };
  }

  private createAreaChartConfig(data: any): ChartConfiguration {
    const config = this.createLineChartConfig(data);
    if (config.data.datasets[0]) {
      // Type assertion for fill property
      (config.data.datasets[0] as any).fill = true;
    }
    return config;
  }

  private createBoxPlotConfig(data: any): ChartConfiguration {
    const boxplotData = data.boxplot || [];
    const labels = data.labels || [this.columnName || 'Data'];
    const yIsDate = data.yIsDate || false;

    return {
      type: 'boxplot' as any,
      data: {
        labels: labels,
        datasets: [{
          label: this.columnName || 'Distribution',
          data: boxplotData,
          backgroundColor: 'rgba(0, 128, 255, 0.5)',
          borderColor: 'rgba(0, 128, 255, 1)',
          borderWidth: 2,
          outlierBackgroundColor: 'rgba(255, 99, 132, 0.5)',
          outlierBorderColor: 'rgba(255, 99, 132, 1)',
          itemRadius: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const raw = context.raw as any;
                if (yIsDate) {
                  return [
                    `Min: ${new Date(raw.min).toLocaleString()}`,
                    `Q1: ${new Date(raw.q1).toLocaleString()}`,
                    `Median: ${new Date(raw.median).toLocaleString()}`,
                    `Q3: ${new Date(raw.q3).toLocaleString()}`,
                    `Max: ${new Date(raw.max).toLocaleString()}`
                  ];
                } else {
                  return [
                    `Min: ${raw.min?.toFixed(2)}`,
                    `Q1: ${raw.q1?.toFixed(2)}`,
                    `Median: ${raw.median?.toFixed(2)}`,
                    `Q3: ${raw.q3?.toFixed(2)}`,
                    `Max: ${raw.max?.toFixed(2)}`
                  ];
                }
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: false, // Scale to data range, not forced to 0
            title: {
              display: true,
              text: this.columnName || 'Value'
            },
            ticks: yIsDate ? {
              callback: (value) => {
                return new Date(value as number).toLocaleDateString();
              }
            } : undefined
          }
        }
      }
    };
  }

  private handleBarClick(event: any, elements: any[], data: any): void {
    if (!elements || elements.length === 0) {
      return;
    }

    const clickedElement = elements[0];
    const dataIndex = clickedElement.index;

    // Get the clicked bar's information
    const labels = data.labels || data.bins?.map((b: any) => b.label) || [];
    const clickedLabel = labels[dataIndex];

    // Build filter based on the clicked bar
    let filter: DataFilter;

    if (data.bins && data.bins[dataIndex]?.range) {
      // For histogram bins with ranges
      const range = data.bins[dataIndex].range;
      filter = {
        column: this.columnName || '',
        operator: 'between',
        value: { min: range.min, max: range.max }
      };
    } else {
      // For categorical bars
      filter = {
        column: this.columnName || '',
        operator: 'equals',
        value: clickedLabel
      };
    }

    this.fetchFilteredData(filter);
  }

  private setupSelectionOverlay(): void {
    if (!this.chartCanvas) return;
    
    const canvas = this.chartCanvas.nativeElement;
    const parent = canvas.parentElement;
    if (!parent) return;
    
    // Create overlay canvas for selection rectangle
    this.selectionCanvas = document.createElement('canvas');
    this.selectionCanvas.style.position = 'absolute';
    this.selectionCanvas.style.top = '0';
    this.selectionCanvas.style.left = '0';
    this.selectionCanvas.style.width = '100%';
    this.selectionCanvas.style.height = '100%';
    this.selectionCanvas.style.pointerEvents = 'none';
    this.selectionCanvas.style.zIndex = '5';
    
    // Set canvas resolution to match the CSS displayed size
    const rect = canvas.getBoundingClientRect();
    this.selectionCanvas.width = rect.width;
    this.selectionCanvas.height = rect.height;
    
    this.selectionContext = this.selectionCanvas.getContext('2d');
    parent.appendChild(this.selectionCanvas);
    
    // Add mouse event listeners
    canvas.addEventListener('mousedown', this.onMouseDown.bind(this));
    canvas.addEventListener('mousemove', this.onMouseMove.bind(this));
    canvas.addEventListener('mouseup', this.onMouseUp.bind(this));
    canvas.addEventListener('mouseleave', this.onMouseLeave.bind(this));
  }
  
  private removeSelectionOverlay(): void {
    if (this.selectionCanvas && this.selectionCanvas.parentElement) {
      this.selectionCanvas.parentElement.removeChild(this.selectionCanvas);
    }
    
    if (this.chartCanvas) {
      const canvas = this.chartCanvas.nativeElement;
      canvas.removeEventListener('mousedown', this.onMouseDown.bind(this));
      canvas.removeEventListener('mousemove', this.onMouseMove.bind(this));
      canvas.removeEventListener('mouseup', this.onMouseUp.bind(this));
      canvas.removeEventListener('mouseleave', this.onMouseLeave.bind(this));
    }
  }
  
  private setupResizeObserver(): void {
    if (!this.chartCanvas || !this.selectionCanvas) return;
    
    const canvas = this.chartCanvas.nativeElement;
    this.resizeObserver = new ResizeObserver(() => {
      this.updateOverlayCanvasDimensions();
    });
    
    this.resizeObserver.observe(canvas);
  }
  
  private updateOverlayCanvasDimensions(): void {
    if (!this.chartCanvas || !this.selectionCanvas || !this.chart) return;
    
    const canvas = this.chartCanvas.nativeElement;
    const rect = canvas.getBoundingClientRect();
    
    // Update overlay canvas resolution
    this.selectionCanvas.width = rect.width;
    this.selectionCanvas.height = rect.height;
    
    // Force Chart.js to resize and update its scales
    this.chart.resize();
    
    // Use setTimeout to ensure Chart.js has completed its resize before we redraw
    setTimeout(() => {
      this.redrawSelectionRectangle();
    }, 0);
  }
  
  private redrawSelectionRectangle(): void {
    // Redraw the selection rectangle using stored data coordinates
    if (!this.selectionContext || !this.selectionCanvas || !this.selectionDataBounds || !this.chart) return;
    
    const xScale = this.chart.scales['x'];
    const yScale = this.chart.scales['y'];
    
    if (!xScale || !yScale) return;
    
    // Convert data coordinates back to pixel coordinates
    const x1Pixel = xScale.getPixelForValue(this.selectionDataBounds.x1);
    const x2Pixel = xScale.getPixelForValue(this.selectionDataBounds.x2);
    const y1Pixel = yScale.getPixelForValue(this.selectionDataBounds.y1);
    const y2Pixel = yScale.getPixelForValue(this.selectionDataBounds.y2);
    
    const startX = Math.min(x1Pixel, x2Pixel);
    const startY = Math.min(y1Pixel, y2Pixel);
    const width = Math.abs(x2Pixel - x1Pixel);
    const height = Math.abs(y2Pixel - y1Pixel);
    
    // Clear and redraw
    this.selectionContext.clearRect(0, 0, this.selectionCanvas.width, this.selectionCanvas.height);
    this.selectionContext.strokeStyle = 'rgba(0, 128, 255, 0.8)';
    this.selectionContext.lineWidth = 2;
    this.selectionContext.fillStyle = 'rgba(0, 128, 255, 0.1)';
    this.selectionContext.fillRect(startX, startY, width, height);
    this.selectionContext.strokeRect(startX, startY, width, height);
    
    // Update pixel coordinates for reference
    this.selectionStart = { x: startX, y: startY };
    this.selectionEnd = { x: startX + width, y: startY + height };
  }
  
  private onMouseDown(event: MouseEvent): void {
    if (!this.chart) return;
    
    const rect = (event.target as HTMLCanvasElement).getBoundingClientRect();
    this.selectionStart = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
    this.isSelecting = true;
  }
  
  private onMouseMove(event: MouseEvent): void {
    if (!this.isSelecting || !this.selectionStart || !this.selectionCanvas || !this.selectionContext) return;
    
    const rect = (event.target as HTMLCanvasElement).getBoundingClientRect();
    this.selectionEnd = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
    
    // Clear and redraw selection rectangle
    this.selectionContext.clearRect(0, 0, this.selectionCanvas.width, this.selectionCanvas.height);
    
    const width = this.selectionEnd.x - this.selectionStart.x;
    const height = this.selectionEnd.y - this.selectionStart.y;
    
    this.selectionContext.strokeStyle = 'rgba(0, 128, 255, 0.8)';
    this.selectionContext.lineWidth = 2;
    this.selectionContext.fillStyle = 'rgba(0, 128, 255, 0.1)';
    this.selectionContext.fillRect(this.selectionStart.x, this.selectionStart.y, width, height);
    this.selectionContext.strokeRect(this.selectionStart.x, this.selectionStart.y, width, height);
  }
  
  private onMouseUp(event: MouseEvent): void {
    if (!this.isSelecting || !this.selectionStart || !this.chart) return;
    
    const rect = (event.target as HTMLCanvasElement).getBoundingClientRect();
    this.selectionEnd = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
    
    // Draw final selection rectangle (keep it visible)
    if (this.selectionContext && this.selectionCanvas) {
      const width = this.selectionEnd.x - this.selectionStart.x;
      const height = this.selectionEnd.y - this.selectionStart.y;
      
      this.selectionContext.clearRect(0, 0, this.selectionCanvas.width, this.selectionCanvas.height);
      this.selectionContext.strokeStyle = 'rgba(0, 128, 255, 0.8)';
      this.selectionContext.lineWidth = 2;
      this.selectionContext.fillStyle = 'rgba(0, 128, 255, 0.1)';
      this.selectionContext.fillRect(this.selectionStart.x, this.selectionStart.y, width, height);
      this.selectionContext.strokeRect(this.selectionStart.x, this.selectionStart.y, width, height);
    }
    
    // Get selected points
    this.handleRectangleSelection();
    
    this.isSelecting = false;
    // Keep selectionStart and selectionEnd to maintain the rectangle
  }
  
  private onMouseLeave(): void {
    if (this.isSelecting && this.selectionContext && this.selectionCanvas) {
      this.selectionContext.clearRect(0, 0, this.selectionCanvas.width, this.selectionCanvas.height);
      this.isSelecting = false;
      this.selectionStart = null;
      this.selectionEnd = null;
    }
    // If not currently dragging, keep the completed selection rectangle visible
  }
  
  private handleRectangleSelection(): void {
    if (!this.chart || !this.selectionStart || !this.selectionEnd || !this.chartData?.data) return;
    
    const data = this.chartData.data;
    const points = data.points || [];
    
    if (points.length === 0) return;
    
    // Convert pixel coordinates to chart data coordinates
    const xScale = this.chart.scales['x'];
    const yScale = this.chart.scales['y'];
    
    if (!xScale || !yScale) return;
    
    const x1 = xScale.getValueForPixel(Math.min(this.selectionStart.x, this.selectionEnd.x));
    const x2 = xScale.getValueForPixel(Math.max(this.selectionStart.x, this.selectionEnd.x));
    const y1 = yScale.getValueForPixel(Math.max(this.selectionStart.y, this.selectionEnd.y)); // Inverted Y axis
    const y2 = yScale.getValueForPixel(Math.min(this.selectionStart.y, this.selectionEnd.y));
    
    // Ensure all values are defined before proceeding
    if (x1 === undefined || x2 === undefined || y1 === undefined || y2 === undefined) return;
    
    // Store data coordinates for redrawing after resize (values are guaranteed to be numbers after the check above)
    this.selectionDataBounds = { 
      x1: x1 as number, 
      x2: x2 as number, 
      y1: y1 as number, 
      y2: y2 as number 
    };
    
    // Filter points within bounds
    const selectedPoints = points.filter((p: any) => {
      return p.x >= (x1 ?? -Infinity) && p.x <= (x2 ?? Infinity) &&
             p.y >= (y1 ?? -Infinity) && p.y <= (y2 ?? Infinity);
    });
    
    if (selectedPoints.length === 0) {
      this.selectedData = [];
      this.displayedColumns = [];
      return;
    }
    
    // Fetch full data for selected points
    this.fetchSelectedScatterData(selectedPoints);
  }
  
  private fetchSelectedScatterData(selectedPoints: any[]): void {
    // Extract indices of selected points
    const indices = selectedPoints.map(p => p.index).filter(i => i !== undefined);
    
    console.log('fetchSelectedScatterData called:', {
      selectedPointsCount: selectedPoints.length,
      indicesCount: indices.length,
      sampleIndices: indices.slice(0, 5),
      hasDataSourceId: !!this.dataSourceId,
      hasUploadedData: !!this.uploadedData,
      uploadedDataLength: this.uploadedData ? (Array.isArray(this.uploadedData) ? this.uploadedData.length : (this.uploadedData as any).data?.length) : undefined
    });
    
    if (indices.length === 0) {
      this.selectedData = [];
      this.displayedColumns = [];
      return;
    }
    
    this.isLoadingData = true;
    this.errorMessage = '';
    
    // Calculate range bounds
    const xValues = selectedPoints.map(p => p.x);
    const yValues = selectedPoints.map(p => p.y);
    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);
    const yMin = Math.min(...yValues);
    const yMax = Math.max(...yValues);
    
    // Check if axes are dates from chart data
    const xIsDate = this.chartData?.data?.xIsDate || false;
    const yIsDate = this.chartData?.data?.yIsDate || false;
    
    // Fetch filtered data
    if (this.dataSourceId && this.columnName && this.secondColumnName) {
      // For datasource, use the range filter endpoint
      const filters = [
        {
          column: this.columnName,
          startValue: this.formatFilterValue(xMin, xIsDate),
          endValue: this.formatFilterValue(xMax, xIsDate)
        },
        {
          column: this.secondColumnName,
          startValue: this.formatFilterValue(yMin, yIsDate),
          endValue: this.formatFilterValue(yMax, yIsDate)
        }
      ];
      
      this.dataService.getDataByRangeFilters(this.dataSourceId, filters).subscribe({
        next: (data) => {
          this.selectedData = data;
          this.updateDisplayedColumns();
          this.isLoadingData = false;
          console.log('Emitting selectionChange (datasource path):', this.selectedData.length, 'rows');
          this.selectionChange.emit(this.selectedData);
        },
        error: (error) => {
          console.error('Error fetching filtered data:', error);
          this.errorMessage = 'Failed to load selected data';
          this.isLoadingData = false;
        }
      });
    } else if (this.uploadedData) {
      // For uploaded data, filter from memory
      // Handle both array and object with data property
      const dataArray = Array.isArray(this.uploadedData) ? this.uploadedData : (this.uploadedData as any).data;
      
      if (!Array.isArray(dataArray)) {
        console.error('uploadedData is not an array and does not have a data property', this.uploadedData);
        this.selectedData = [];
        this.displayedColumns = [];
        this.isLoadingData = false;
        this.errorMessage = 'Invalid uploaded data format';
        return;
      }
      
      console.log('Filtering uploadedData with indices:', indices);
      this.selectedData = dataArray.filter((row: any, index: number) => {
        return indices.includes(index);
      });
      console.log('Filtered result:', this.selectedData.length, 'rows');
      this.updateDisplayedColumns();
      this.isLoadingData = false;
      console.log('Emitting selectionChange (uploadedData path):', this.selectedData.length, 'rows');
      this.selectionChange.emit(this.selectedData);
    } else {
      // No data source available
      this.selectedData = [];
      this.displayedColumns = [];
      this.isLoadingData = false;
      this.errorMessage = 'No data source available';
    }
  }
  
  /**
   * Format a filter value - convert timestamps back to ISO date strings if needed
   */
  private formatFilterValue(value: number, isDate: boolean): string {
    if (isDate) {
      // Convert timestamp to ISO date string (YYYY-MM-DD format)
      return new Date(value).toISOString().split('T')[0];
    }
    return value.toString();
  }
  
  private handleScatterClick(event: any, elements: any[], data: any): void {
    // Removed - selection now handled by rectangle drag
    return;
  }

  private fetchFilteredData(filter: DataFilter): void {
    this.isLoadingData = true;
    this.errorMessage = '';

    // If we have uploaded data, filter it locally
    if (this.uploadedData) {
      const dataArray = Array.isArray(this.uploadedData) ? this.uploadedData : (this.uploadedData as any).data;
      if (Array.isArray(dataArray)) {
        this.selectedData = this.filterDataLocally(dataArray, filter);
        this.updateDisplayedColumns();
        this.isLoadingData = false;
        this.selectionChange.emit(this.selectedData);
        return;
      }
    }

    // Otherwise, fetch from API
    if (!this.dataSourceId) {
      this.errorMessage = 'No data source available';
      this.isLoadingData = false;
      return;
    }

    this.dataService.getFilteredData(this.dataSourceId, filter).subscribe({
      next: (data) => {
        this.selectedData = data;
        this.updateDisplayedColumns();
        this.isLoadingData = false;
        this.selectionChange.emit(data);
      },
      error: (error) => {
        console.error('Error fetching filtered data:', error);
        this.errorMessage = 'Failed to load filtered data';
        this.isLoadingData = false;
        this.chartError.emit(this.errorMessage);
      }
    });
  }

  private fetchDataByIndices(indices: number[]): void {
    if (this.uploadedData) {
      const dataArray = Array.isArray(this.uploadedData) ? this.uploadedData : (this.uploadedData as any).data;
      if (Array.isArray(dataArray)) {
        this.selectedData = indices.map(i => dataArray[i]).filter(d => d !== undefined);
        this.updateDisplayedColumns();
        this.selectionChange.emit(this.selectedData);
      }
    }
  }

  private filterDataLocally(data: any[], filter: DataFilter): any[] {
    return data.filter(row => {
      const value = row[filter.column];

      switch (filter.operator) {
        case 'equals':
          return value?.toString() === filter.value?.toString();
        case 'between':
          const numValue = parseFloat(value);
          return !isNaN(numValue) && 
                 numValue >= filter.value.min && 
                 numValue <= filter.value.max;
        case 'in':
          return filter.value.includes(value?.toString());
        case 'greater_than':
          return parseFloat(value) > filter.value;
        case 'less_than':
          return parseFloat(value) < filter.value;
        default:
          return true;
      }
    });
  }

  private updateDisplayedColumns(): void {
    if (this.selectedData.length > 0) {
      this.displayedColumns = Object.keys(this.selectedData[0]);
    }
  }

  clearSelection(): void {
    this.selectedData = [];
    this.displayedColumns = [];
    this.selectionChange.emit([]);
    
    // Clear the selection rectangle
    if (this.selectionContext && this.selectionCanvas) {
      this.selectionContext.clearRect(0, 0, this.selectionCanvas.width, this.selectionCanvas.height);
    }
    this.selectionStart = null;
    this.selectionEnd = null;
    this.selectionDataBounds = null;
  }
}
